import SceneObject from "../../../lib/services/scene/sceneobject";
import ShaderService from "../../../lib/services/shader/shader_service";
import ResourceService from "../../../lib/services/resource/resourceservice";
import Camera from "../../../lib/services/scene/camera";
import Bone from "../bone/bone";
import Puppy from "./puppy";
import { PuppyClips } from "./puppyclips";
import {FetchPlan} from "./fetch_types"
import Stage from "../../../lib/services/stage/stage";
import Transform from "../../../lib/services/render/transform";

const BONE_SPAWN_POINTS: readonly Float32Array[] = [
  new Float32Array([3.2, 0, 0.0]),
  new Float32Array([2.4, 0, -3.6]),
  new Float32Array([-3.8, 0, -2.0]),
  new Float32Array([-2.0, 0, 4.0]),
  new Float32Array([0.6, 0, 3.4]),
  new Float32Array([4.0, 0, -4.5]),
];

type BoneToss = {
  start: Float32Array;
  end: Float32Array;
  duration: number;
  apex_height: number;
  elapsed: number;
};

type FetchPhase =
  | "idle"
  | "running_to_bone"
  | "returning_to_eat_spot"
  | "arrive_transition"
  | "deliver_reward"
  | "cleanup";

const EAT_SPOT = new Float32Array([0.5132098197937012, 0, 6.3]);
const TOSS_DURATION = 3.2;
const TOSS_APEX_HEIGHT = 0.9;
const TOSS_ORIGIN = new Float32Array([0.0, 1.0, 3.5]);
const DELIVER_REWARD_DURATION = 1.5;
const DROP_BONE_DURATION = 0.75;
const DROP_DURATION = 0.5;
const DROP_APEX_HEIGHT = 0.05;

export default class BoneFetchController extends SceneObject {

  private bone_count: number = 0;

  private device: GPUDevice | null = null;
  private format: GPUTextureFormat | null = null;
  private shader_service: ShaderService | null = null;
  private resource_service: ResourceService | null = null;

  private spawn_points_pool: number[] = [];
  private phase: FetchPhase = "idle";
  private active_bone: Bone | null = null;
  private active_toss: BoneToss | null = null;
  private puppy: Puppy;

  private timer:number =0;
  private bone_has_dropped:boolean = false;
  private bone_drop_dest:Transform;
  public  enabled:boolean = false; 

  constructor(key: string, puppy: Puppy) {
    super(key);
    this.puppy = puppy;
    this.bone_drop_dest = new Transform();
  }

  get_phase(): FetchPhase {
    return this.phase;
  }

  get_bone_count(): number {
    return this.bone_count;
  }
  add_bones(count:number):void
  {
    this.bone_count+= count;
  }
  

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService
  ): Promise<void> {
    this.device = device;
    this.format = format;
    this.shader_service = shader_service;
    this.resource_service = resource_service;

    await super.build(device, format, shader_service, resource_service);
  }


  ui_message(cmd: string, msg: string): void {
    if (cmd === "toss_bone") 
    {
          this.try_toss();
    }
    super.ui_message(cmd, msg);
  }

  tick(delta_time: number): void {
    super.tick(delta_time);
    if(!this.enabled) return;

    if (this.active_toss && this.active_bone) {
      const finished = this.update_toss(this.active_toss, this.active_bone, delta_time);
      if (finished) {
        this.finish_toss();
      }
    }

    switch (this.phase) {
      case "arrive_transition":
        this.tick_arrive_transition();
        break;
      case "running_to_bone":
        this.tick_running_to_bone();
        break;
      case "returning_to_eat_spot":
        this.tick_returning_to_eat_spot();
        break;
      case "cleanup":
        this.tick_cleanup();
        break;
       case "deliver_reward":
        this.tick_deliver_reward(delta_time);
        break;
      case "idle":
      default:
        break;
    }
  }

  private update_toss(toss: BoneToss, bone: Bone, delta_time: number): boolean {
    toss.elapsed += delta_time;
    const t = Math.min(toss.elapsed / toss.duration, 1.0);

    const x = toss.start[0] + (toss.end[0] - toss.start[0]) * t;
    const z = toss.start[2] + (toss.end[2] - toss.start[2]) * t;

    const base_y = toss.start[1] + (toss.end[1] - toss.start[1]) * t;
    const arc_y = 4 * toss.apex_height * t * (1 - t);
    const y = base_y + arc_y;

    bone.transform.set_position(x, y, z);
    bone.transform.set_rotation(
      toss.elapsed * 3.7,
      toss.elapsed * 5.1,
      toss.elapsed * 2.3
    );
    bone.transform.update_matrix();

    return t >= 1.0;
  }

  private finish_toss(): void {
    if (!this.active_toss || !this.active_bone) return;

    this.active_bone.transform.set_position(
      this.active_toss.end[0],
      0.0,
      this.active_toss.end[2]
    );
    this.active_bone.transform.set_rotation(
      0,
      Math.random() * Math.PI * 2,
      0
    );
    this.active_bone.transform.update_matrix();
    this.active_toss = null;
    

    
  }

  private tick_running_to_bone(): void {
    if (this.active_toss !== null) return;

    if (!this.active_bone) {
      this.phase = "cleanup";
      return;
    }

    if (this.puppy.navigator.has_destination()) return;

    this.attach_bone_to_puppy();
    this.phase = "returning_to_eat_spot";

    this.puppy.navigator.move_to_point(EAT_SPOT, "aggressive", 0.1, {
      oneShots: [PuppyClips.Sitting_start],
      finalLoop: PuppyClips.Sitting_loop_2,
    });
  }

private tick_returning_to_eat_spot(): void {
  if (this.puppy && this.puppy.animation_controller_gpu) {
    const animState = this.puppy.animation_controller_gpu.get_state();

    const activeClipIndex =
      animState.nextState?.clipIndex ?? animState.currentState?.clipIndex ?? null;
    const activeProgress =
      animState.nextState?.progress ?? animState.currentState?.progress ?? null;

    if (
      activeClipIndex === PuppyClips.Sitting_loop_2 &&
      activeProgress !== null &&
      activeProgress <= 0.5
    ) {
      this.phase = "arrive_transition";
      return;
    }
  }

  if (this.puppy.navigator.has_destination()) return;


  this.phase = "cleanup";
}

  private tick_arrive_transition(): void {
    if (this.puppy.navigator.has_destination()) return;

    this.timer = 0;
    this.bone_has_dropped = false;
    this.phase = "deliver_reward";
  }

  private tick_deliver_reward(delta_time:number)
  {
       this.timer += delta_time;
       if(this.bone_has_dropped && this.active_toss && this.active_bone)
       {
            this.update_toss(this.active_toss,this.active_bone,delta_time);
       }
       if( this.active_bone &&  ! this.bone_has_dropped && this.timer > DROP_BONE_DURATION)
       {
           this.place_bone_in_front_of_puppy();
           this.active_toss = {
            start: new Float32Array(this.active_bone?.transform.position),
            end:  this.bone_drop_dest.position,
            duration: DROP_DURATION,
            apex_height: DROP_APEX_HEIGHT,
            elapsed: 0,
          };
           
           this.bone_has_dropped = true;
       }

        
       if(this.timer > DELIVER_REWARD_DURATION)
       {
             Stage.instance().scene_service.ui_message("reward","bone");
             this.phase = "cleanup";
       }
  }

  private tick_cleanup(): void 
  {
    if (this.active_bone)
    {
      //this.remove_child(this.active_bone);
      //this.active_bone = null;
    }
  
    this.active_toss = null;
    this.phase = "idle";
  }

  private place_bone_in_front_of_puppy(): void {
  if (!this.active_bone) return;

  this.puppy.detach_child(this.active_bone);

  const forward = this.puppy.transform.get_forward();
  const drop_distance = 0.05;
  const p = this.active_bone.transform.position;
  
  this.bone_drop_dest.set_position(
    p[0] + forward[0] * drop_distance,
    0.0,
    p[2] + forward[2] * drop_distance
  );
   this.bone_drop_dest.update_matrix();

}

  private attach_bone_to_puppy(): void {
    if (!this.active_bone) return;
    this.puppy.attach_child(this.active_bone,"mouth");


  }

  private next_spawn_point(): Float32Array {
    if (this.spawn_points_pool.length === 0) {
      this.spawn_points_pool = BONE_SPAWN_POINTS.map((_, i) => i);
      this.shuffle(this.spawn_points_pool);
    }

    const idx = this.spawn_points_pool.pop()!;
    return BONE_SPAWN_POINTS[idx];
  }

  private shuffle<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }

  async try_toss(): Promise<void> {
    if (this.bone_count <= 0) return;
    if (this.phase !== "idle") return;
    if (!this.device || !this.format || !this.shader_service || !this.resource_service) {
      return;
    }

    this.bone_count -= 1;

    const landing = this.next_spawn_point();

    if(this.active_bone)
    {
        this.remove_child(this.active_bone);
       this.active_bone = null;
    }

    const key = `bone_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    this.active_bone = Bone.random(key);

    await this.active_bone.build(
      this.device,
      this.format,
      this.shader_service,
      this.resource_service
    );

    this.active_bone.transform.set_position(TOSS_ORIGIN[0], TOSS_ORIGIN[1], TOSS_ORIGIN[2]);
    this.active_bone.transform.set_scale(0.5, 0.5, 0.5);
    this.active_bone.transform.update_matrix();

    const target = new Float32Array([landing[0], 0.0, landing[2]]);
    this.active_toss = {
      start: new Float32Array(TOSS_ORIGIN),
      end: target,
      duration: TOSS_DURATION,
      apex_height: TOSS_APEX_HEIGHT,
      elapsed: 0,
    };

    this.phase = "running_to_bone";

    this.puppy.navigator.move_to_point(target, "aggressive", 0.1, {
        oneShots: [
          PuppyClips.EatDrink_start,
        ],
        finalLoop:PuppyClips.Eat_loop,
      });

      this.add_child(this.active_bone);
  }

  get_bone_target_position(): Float32Array {
    if (this.active_toss && this.active_toss.end) {
      return this.active_toss.end;
    }
    return new Float32Array([0, 0, 0]);
  }
}